import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface User {
  email: string;
  role: 'admin' | 'manager' | 'exclusive' | 'USER' | 'DEALER';
  name: string;
  id?: string | number;
}

interface AuthState {
  user: User | null;
  token: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Separate localStorage keys so Admin and User can coexist in the same browser
// ─────────────────────────────────────────────────────────────────────────────
const ADMIN_ROLES = ['admin', 'manager', 'exclusive', 'AU', 'SU'] as const;

const isAdminRole = (role: string): boolean =>
  ADMIN_ROLES.includes(role as typeof ADMIN_ROLES[number]);

export const ADMIN_TOKEN_KEY = 'diasporawise_admin_token';
export const ADMIN_USER_KEY  = 'diasporawise_admin_user';
export const USER_TOKEN_KEY  = 'diasporawise_user_token';
export const USER_DATA_KEY   = 'diasporawise_user_data';

const initialState: AuthState = {
  user: null,
  token: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    login: (state, action: PayloadAction<{ user: User; token: string }>) => {
      state.user  = action.payload.user;
      state.token = action.payload.token;

      if (isAdminRole(action.payload.user.role)) {
        // Admin/manager login — persist in ADMIN keys only
        localStorage.setItem(ADMIN_TOKEN_KEY, action.payload.token);
        localStorage.setItem(ADMIN_USER_KEY,  JSON.stringify(action.payload.user));
      } else {
        // Regular user / dealer login — persist in USER keys only
        localStorage.setItem(USER_TOKEN_KEY, action.payload.token);
        localStorage.setItem(USER_DATA_KEY,  JSON.stringify(action.payload.user));
      }
    },

    logout: (state) => {
      const role  = state.user?.role;
      state.user  = null;
      state.token = null;

      if (role && isAdminRole(role)) {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        localStorage.removeItem(ADMIN_USER_KEY);
      } else {
        localStorage.removeItem(USER_TOKEN_KEY);
        localStorage.removeItem(USER_DATA_KEY);
      }
    },

    restoreAuth: (state) => {
      const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
      const adminUser  = localStorage.getItem(ADMIN_USER_KEY);
      const userToken  = localStorage.getItem(USER_TOKEN_KEY);
      const userData   = localStorage.getItem(USER_DATA_KEY);

      // Route-aware session restoration:
      // Admin routes (/admin/*, /super-admin/*) -> load admin session into Redux.
      // All other routes (/user/*, /farm-sponsorship, etc.) -> load user session.
      // This lets both admin and user stay logged in simultaneously in the same
      // browser tab without each other's token causing conflicts.
      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      const isAdminRoute = path.startsWith('/admin') || path.startsWith('/super-admin');

      if (isAdminRoute) {
        // On admin routes: prefer admin token
        if (adminToken && adminUser) {
          state.token = adminToken;
          state.user  = JSON.parse(adminUser);
        } else if (userToken && userData) {
          state.token = userToken;
          state.user  = JSON.parse(userData);
        }
      } else {
        // On user/public routes: prefer user token
        if (userToken && userData) {
          state.token = userToken;
          state.user  = JSON.parse(userData);
        } else if (adminToken && adminUser) {
          state.token = adminToken;
          state.user  = JSON.parse(adminUser);
        }
      }
    },
  },
});

export const { login, logout, restoreAuth } = authSlice.actions;
export default authSlice.reducer;